// Central registry of toggleable pages. Used by:
//  - /admin/pages       (renders switches)
//  - App.tsx PageGate   (redirects disabled routes)
//  - AppSidebar.tsx     (hides sidebar entries)
//
// Add a page here + insert its key into `page_visibility` (default true)
// and it becomes admin-toggleable everywhere at once.

export type PageKey =
  | "feed"
  | "podcasts"
  | "social"
  | "tokens"
  | "intelligence"
  | "prediction-markets"
  | "leaderboard"
  | "learn"
  | "games"
  | "bookmarks"
  | "weekly-brief"
  | "premium";

export interface PageDefinition {
  key: PageKey;
  label: string;
  description: string;
  section: "Content" | "Markets" | "Intelligence" | "Community" | "Tools";
  routes: string[]; // all paths gated by this key
  sidebarUrl?: string; // matches AppSidebar item.url, if any
  /**
   * State used when the page has no row in `page_visibility` yet.
   * Lets a page ship switched off while staying admin-toggleable.
   * Defaults to true.
   */
  defaultEnabled?: boolean;
}

export const PAGE_REGISTRY: PageDefinition[] = [
  { key: "feed", label: "Feed", description: "Community feed at /feed.", section: "Content", routes: ["/feed"], sidebarUrl: "/feed" },
  { key: "podcasts", label: "Podcasts", description: "Podcast index and episode pages.", section: "Content", routes: ["/podcasts", "/podcasts/:podcastId", "/podcasts/:podcastId/episodes/:episodeId"], sidebarUrl: "/podcasts" },
  { key: "social", label: "Social", description: "Creators we follow on TikTok and Instagram at /social.", section: "Content", routes: ["/social"], sidebarUrl: "/social" },
  { key: "weekly-brief", label: "Weekly Brief", description: "The weekly editorial brief page.", section: "Content", routes: ["/weekly", "/weekly/:id"], sidebarUrl: "/weekly" },
  { key: "tokens", label: "Markets", description: "Markets list and asset detail pages.", section: "Markets", routes: ["/markets", "/markets/:id"], sidebarUrl: "/markets" },
  { key: "prediction-markets", label: "Prediction Markets", description: "Kalshi + Polymarket hub.", section: "Markets", routes: ["/prediction-markets", "/prediction-markets/:eventId"], sidebarUrl: "/prediction-markets" },
  { key: "intelligence", label: "Intelligence Pro", description: "All /intelligence/* pages (forecasts, analysts, leaderboard, etc.).", section: "Intelligence", routes: ["/intelligence", "/intelligence/*"], sidebarUrl: "/intelligence", defaultEnabled: false },
  { key: "leaderboard", label: "Leaderboard", description: "Global contribution leaderboard.", section: "Community", routes: ["/leaderboard"], sidebarUrl: "/leaderboard" },
  { key: "bookmarks", label: "Bookmarks", description: "User's saved articles.", section: "Tools", routes: ["/bookmarks"], sidebarUrl: "/bookmarks" },
  { key: "learn", label: "Learn", description: "Courses and learning paths.", section: "Content", routes: ["/learn", "/learn/:pathId"], sidebarUrl: "/learn" },
  { key: "games", label: "Games", description: "Games hub + multiplayer rooms.", section: "Content", routes: ["/games", "/games/:slug", "/games/:slug/play"], sidebarUrl: "/games" },
  { key: "premium", label: "Premium", description: "Subscription upsell page (currently hidden).", section: "Tools", routes: ["/premium"], defaultEnabled: false },
];

export const PAGE_KEYS: PageKey[] = PAGE_REGISTRY.map((p) => p.key);

/** Map sidebar URL -> pageKey so AppSidebar can filter. */
export const SIDEBAR_URL_TO_PAGE_KEY: Record<string, PageKey> = Object.fromEntries(
  PAGE_REGISTRY.filter((p) => p.sidebarUrl).map((p) => [p.sidebarUrl!, p.key])
) as Record<string, PageKey>;

/** Fallback state for a page that has no row in `page_visibility` yet. */
export const PAGE_DEFAULT_ENABLED: Record<PageKey, boolean> = Object.fromEntries(
  PAGE_REGISTRY.map((p) => [p.key, p.defaultEnabled ?? true])
) as Record<PageKey, boolean>;
